<template>
    <div>
        <b-btn-group v-if="isDocument()" role="tab" >
            <b-button v-if="isDocument && params.data.hasFile && params.data.acl.downloadable"
                      variant="outline-secondary"
                      v-b-tooltip.hover.d250="$t('files.context.download')"
                      @click="downloadFile(params.data.id)"
                      v-show="projectPermissions.includes('DOWNLOAD_FILE')"
                      :disabled="(numberOfSelectedFiles > 1)">
                <icon name="download"></icon>
            </b-button>
            <b-button v-if="params.data.acl.changeFile" 
                      variant="outline-secondary"
                      v-b-tooltip.hover.d250="$t('files.context.replace')"
                      @click="replaceFile(params.data)"
                      v-show="projectPermissions.includes('REPLACE_FILE')">
                <icon name="upload"></icon>
            </b-button>
            <b-button
                variant="outline-secondary"
                v-b-tooltip.hover.d250="$t('files.context.createtask')"
                @click="openCreateTaskModal(params.data.id)"
                v-show="projectPermissions.includes('CREATE_TASK') && projectData.showTasks">
                <icon name="tasks"></icon>
            </b-button>
            <b-button variant="outline-secondary"
                    v-b-tooltip.hover.d250="$t('files.context.createmessage')"
                    @click="openCreateMessageModal(params.data.id)" v-show="projectPermissions.includes('CREATE_MESSAGE')">
                <icon name="comment"></icon>
            </b-button>
            <b-button v-if="params.data.acl.downloadable && params.data.acl.sharable"
                      variant="outline-secondary"
                      :class="{'share-indicator': isShared}"
                      v-b-tooltip.hover.d250="$t('files.context.share')"
                      @click="openShareModal(params.data.id)"
                      v-show="projectPermissions.includes('SHARE_FILE')">
                <icon name="share-alt"></icon>
            </b-button>
            <b-button v-if="params.data.acl.deletable" 
                      variant="outline-secondary"
                      v-b-tooltip.hover.d250="$t('files.context.deleteDocument')"
                      @click="deleteFile(params.data.id, params.data.objectTypeId)"
                      v-show="projectPermissions.includes('DELETE_FILE')">
                <icon name="trash"></icon>
            </b-button>
            <b-button v-if="params.data.acl.changeIndex" 
                      variant="outline-secondary"
                      v-b-tooltip.hover.d250="$t('files.context.editDocument')"
                      @click="renameFile(params.data)"
                      v-show="projectPermissions.includes('RENAME_FILE')">
                <icon name="pencil"></icon>
            </b-button>
            <b-button v-if="isOnlyOfficeEnabled()"
                      variant="outline-secondary"
                      v-b-tooltip.hover.d250="$t('files.context.onlyOffice')"
                      @click="gotoOnlyOffice(params.data)">
                <icon name="external-link"></icon>
            </b-button>
        </b-btn-group>
    </div>
</template>

<script>
import Vue from 'vue';
import {mapGetters, mapState} from 'vuex';
import i18n from '@/i18n.bootstrap';
import utils from '../../../Utils.js';

export default Vue.extend({
    data() {
        return {
            showCreateTaskModal: false
        };
    },

    computed: {
        baseUrl() {
            return `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
        },
        ...mapGetters({
            globalConfig: 'system/globalConfig',
            projectData: 'projects/projectData',
            isGuestUser_Actions: 'projects/isGuestUser'
        }),
        ...mapState('projects', {
          numberOfSelectedFiles: state => state.currentProject.numberOfSelectedFiles
        }),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
        isShared() {
            return this.params.data.sharedId !== '';
        }
    },

    methods: {
        isDocument() {
            if (this.params) {
                return this.params.data.type.toUpperCase() === 'DOCUMENT';
            }
        },
        isOnlyOfficeEnabled() {
            if(this.params && this.globalConfig.onlyofficeMimeTypes) {
                return this.globalConfig.onlyofficeMimeTypes.includes(this.params.data.mimeType);
            } else {
                return false;
            }
        },
        gotoOnlyOffice(data) {
            let projectId = this.$route.params.projectId;
            let url = this.baseUrl + `/colab/epr/onlyoffice/${projectId}/${data.objectTypeId}/${data.id}`;
            window.open(url, '_blank');
        },
        downloadFile(fileId) {
            let { projectId } = this.$route.params;
            let rawFileTitle = `${this.params.data.title}.${utils.getFileTypeExtension(this.params.data).toLowerCase()}`;
            let encodedFileTitle = encodeURIComponent(rawFileTitle);
            
            let url = `/colab/epr/projects/${projectId}/content/${fileId}?title=${encodedFileTitle}`;
            window.location.href = url;
        },

        deleteFile(fileId, objectTypeId) {
            let projectId = this.$route.params.projectId;
            let indexData = {
                location: this.$route.query.pathIndex.split(',').pop()
            }
            const deleteFileAction = () => {
                this.$store.dispatch('projects/deleteFile', { indexData, projectId, fileId, objectTypeId })
                    .then(() => {
                        this.$root.$emit('file-actions::file-deleted', { fileId: fileId });
                    });
            };

            this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: deleteFileAction },
                    { text: i18n.t('general.no') }
                ]
            });
        },

        renameFile(file) {
            this.$root.$emit('file-actions::open-rename-file-modal', { file: file });
        },

        replaceFile(file) {
            this.$root.$emit('file-actions::open-replace-file-modal', { file: file });
        },

        openCreateTaskModal(fileId) {
            this.$root.$emit('file-actions::open-create-task-modal', { fileId: fileId });
        },

        openCreateMessageModal(fileId) {
            this.$root.$emit('file-actions::open-create-message-modal', { fileId: fileId });
        },

        openShareModal(fileId) {
            this.$root.$emit('file-actions::open-share-modal', { fileId: fileId });
        }
    }
});
</script>

<style lang="scss" scoped>
    .btn-group {
        vertical-align: unset!important;

        button {
            padding-top: 0.16em;
            padding-bottom: 0;
            border: none!important;
        }
    }
    
    .share-indicator {
        background-color: #0180c7;
        color: white;

        &:hover {
            background-color: #60b0e0;
            color: white;
        }
    }
</style>

